/**
 * @file NutritionalView.jsx
 * @author Fernando Ruano
 * @description Nutritional View Page
 */

// Dependencies
import React from 'react';
import { Empty } from 'antd';
import { groupBy, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import moment from 'moment';

// Components
import AppCardLayout from '@nephro/components/AppCardLayout';

// Assets
import { ReactComponent as Apple } from '@nephro/assets/Apple.svg';
import { ReactComponent as WeightPreHigh } from '@nephro/assets/IMC_Preobesidad.svg';
import { ReactComponent as WeightLow } from '@nephro/assets/IMC_Delgadez.svg';
import { ReactComponent as WeightNormal } from '@nephro/assets/IMC_Normal.svg';
import { ReactComponent as WeightHigh } from '@nephro/assets/IMC_Obesidad.svg';

// Helpers
import { onGetWeightType } from '@nephro/commons/helpers/utils';
import { NUTRITIONAL_INDICATOR } from '../helpers/constants';

// Hooks
import usePatientIndicators from '../helpers/usePatientIndicators';

function NutritionalView({ actions }) {
  const nutritionalData = usePatientIndicators(NUTRITIONAL_INDICATOR);
  const [nutritionalItems, setNutritionalItems] = React.useState(null);
  const { setLoading } = actions;

  const nutritionalStatus = [
    {
      status: 'low',
      text: 'Delgadez',
      color: 'stage-3b',
      icon: <WeightLow />
    },
    {
      status: 'normal',
      text: 'Normal',
      color: 'stage-1',
      icon: <WeightNormal />
    },
    {
      status: 'preHigh',
      text: 'Preobesidad',
      color: 'stage-2',
      icon: <WeightPreHigh />
    },
    {
      status: 'high',
      text: 'Obesidad',
      color: 'stage-3b',
      icon: <WeightHigh />
    }
  ];

  React.useEffect(() => {
    setLoading(true);
    if (nutritionalData && !isEmpty(nutritionalData)) {
      const groupByUnity = groupBy(nutritionalData, 'unidad');
      setNutritionalItems(groupByUnity);
      setLoading(false);
    }
  }, [nutritionalData]);

  const measurementBlock = (text, value) => (
    <div className="w-48per bg-disabled pt-2 pb-2 rounded-lg ">
      <span className="text-secondary bg-disabled w-full">
        {text}: <strong>{value}</strong>
      </span>
    </div>
  );

  const formatDate = (date) => moment(date).format('DD/MM/YYYY');

  const onGetWeightItem = (val) => {
    const [weightItm] = nutritionalStatus.filter(
      (itm) => onGetWeightType(val) === itm.status
    );
    return weightItm;
  };

  return (
    <AppCardLayout>
      <div
        style={{ fontSize: 18 }}
        className="text-center p-5 items-center m-auto text-baxterGray mt-1">
        <div className="flex flex-row items-center">
          <Apple width={40} />
          <h3 className="text-xl text-left mb-0 ml-2 text-baxterGray font-bold">
            Estado nutricional
          </h3>
        </div>
        {isEmpty(nutritionalData) ? (
          <div className="mt-40">
            <Empty
              style={{ color: '#858fab', fontSize: 15 }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No hay registro de datos"
            />
          </div>
        ) : (
          <div>
            <h3 className="text-base text-left text-baxterGray mt-2">
              El siguiente gráfico muestra su último valor de índice de masa
              corporal (IMC)
            </h3>
            {nutritionalItems && (
              <section className="flex flex-col md:items-center">
                <section className="flex flex-wrap mb-10 mt-10 items-center md:w-3/4">
                  <div className="w-1/2">
                    <div className="flex justify-center items-center md:justify-end md:pr-20">
                      {
                        onGetWeightItem(nutritionalItems['kg/m²'][0]?.valor)
                          ?.icon
                      }
                    </div>
                  </div>
                  <div className="w-1/2 flex flex-col items-center md:items-start md:pl-10">
                    <div className="flex flex-row items-center justify-center text-center flex-wrap">
                      <h1
                        className={`mb-0 text-3xl md:text-4xl font-bold text-${
                          onGetWeightItem(nutritionalItems['kg/m²'][0]?.valor)
                            ?.color
                        }`}>
                        IMC {nutritionalItems['kg/m²'][0]?.valor}
                      </h1>
                      <h1
                        className={`mb-0 text-xl md:text-2xl ml-2 text-${
                          onGetWeightItem(nutritionalItems['kg/m²'][0]?.valor)
                            ?.color
                        }`}>
                        kg/m²
                      </h1>
                    </div>
                    <div
                      style={{ padding: '1px 16px 1px 16px', marginTop: 15 }}
                      className={`text-xl md:ml-12 w-max text-white font-bold bg-${
                        onGetWeightItem(nutritionalItems['kg/m²'][0]?.valor)
                          ?.color
                      } rounded-full justify-center`}>
                      {
                        onGetWeightItem(nutritionalItems['kg/m²'][0]?.valor)
                          ?.text
                      }
                    </div>
                  </div>
                </section>

                <section className="flex justify-between mb-7 md:w-2/4">
                  {measurementBlock(
                    'Talla',
                    `${nutritionalItems?.m[0]?.valor} m`
                  )}
                  {measurementBlock(
                    'Peso',
                    `${nutritionalItems?.Kg[0]?.valor} Kg`
                  )}
                </section>
              </section>
            )}

            {nutritionalItems && (
              <section className="flex flex-col md:items-center">
                <div className="p-1 py-3 text-left rounded-lg border border-gray-400 mt-4 md:w-2/4">
                  <section className="mb-4">
                    <span className="ml-2">
                      Último: {formatDate(nutritionalItems['kg/m²'][0]?.fecha)}
                    </span>
                    <span className="float-right mr-2">
                      IMC <strong>{nutritionalItems['kg/m²'][0]?.valor}</strong>{' '}
                      kg/m²
                    </span>
                  </section>
                  <section>
                    <span className="ml-2">
                      Anterior:{' '}
                      {formatDate(nutritionalItems['kg/m²'][1]?.fecha)}
                    </span>
                    <span className="float-right mr-2">
                      IMC <strong>{nutritionalItems['kg/m²'][1]?.valor}</strong>{' '}
                      kg/m²
                    </span>
                  </section>
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </AppCardLayout>
  );
}

/**
 * Component definition of proptypes.
 */
NutritionalView.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool
};

export default NutritionalView;
